import { Link } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Lock, ArrowRight, CheckCircle2, Loader2, Zap } from "lucide-react";

/**
 * ProGate — wraps a Maker Pro-only feature. Shows a warm upgrade screen when
 * the current user is on the free tier; renders `children` when they're Pro
 * or on an active trial. Also offers a one-tap 7-day free trial to eligible
 * vendors who haven't used theirs yet.
 */
export default function ProGate({ feature, pitch, perks, children }) {
  const { user, loading, refresh } = useAuth();
  const [startingTrial, setStartingTrial] = useState(false);

  if (loading || user === null) {
    return (
      <div className="mx-auto max-w-6xl px-6 py-24 text-center"
            style={{ color: "var(--ink-soft)" }}
            data-testid="pro-gate-loading">
        <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
      </div>
    );
  }

  // Active pass: Pro subscriber OR active trial
  const hasAccess = user && (user.plan === "pro" || user.on_trial);
  if (hasAccess) return children;

  const canStartTrial = !!user?.trial_available;

  const startTrial = async () => {
    setStartingTrial(true);
    try {
      await api.post("/vendor/start-trial");
      await refresh?.();
      toast.success("Your 7-day free trial is on. Enjoy Maker Pro.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setStartingTrial(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-16" data-testid="pro-gate">
      <div
        className="card-warm rounded-lg overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(31,93,52,0.10), rgba(201,162,75,0.10))",
          borderLeft: "3px solid var(--forest)",
        }}
      >
        <div className="p-8 sm:p-12">
          <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--terracotta)" }}>
            <Lock className="h-3 w-3" /> Maker Pro
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3">
            {feature} is part of Maker Pro.
          </h1>
          {pitch && (
            <p className="mt-4 text-base max-w-xl" style={{ color: "var(--ink-soft)" }}>
              {pitch}
            </p>
          )}
          {perks && perks.length > 0 && (
            <ul className="mt-6 space-y-2.5" data-testid="pro-gate-perks">
              {perks.map((perk, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "var(--forest)" }} />
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-3">
            {canStartTrial ? (
              <>
                <button
                  type="button"
                  onClick={startTrial}
                  disabled={startingTrial}
                  data-testid="pro-gate-start-trial-btn"
                  className="btn-primary rounded-full px-6 py-3 inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {startingTrial
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Zap className="h-4 w-4" />}
                  Start 7-day free trial
                  <ArrowRight className="h-4 w-4" />
                </button>
                <Link
                  to="/pricing"
                  data-testid="pro-gate-upgrade-btn"
                  className="text-sm underline"
                  style={{ color: "var(--ink-soft)" }}
                >
                  or skip trial and upgrade now
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/pricing"
                  data-testid="pro-gate-upgrade-btn"
                  className="btn-primary rounded-full px-6 py-3 inline-flex items-center gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  Upgrade to Maker Pro
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/vendor/dashboard"
                  className="text-sm underline"
                  style={{ color: "var(--ink-soft)" }}
                  data-testid="pro-gate-back-btn"
                >
                  Back to dashboard
                </Link>
              </>
            )}
          </div>

          <div className="mt-6 text-xs" style={{ color: "var(--ink-soft)" }}>
            {canStartTrial
              ? "No card required to start your trial. Cancel anytime."
              : user?.trial_started_at
                ? "Your free trial has ended — upgrade any time to keep everything."
                : "You're currently on the Free plan. Upgrade any time, cancel anytime."}
          </div>
        </div>
      </div>
    </div>
  );
}
